import { repo } from 'src/services/http';

/**
 * Get browser support information for WebRTC
 *
 * @returns {Array<{ title: string; values: string[] }>} Returns an array of objects containing the codec name and its parameters
 */
export const getBrowserMediaSupport = (): {
	title: string;
	values: string[];
}[] => {
	const mediaSupport = [
		...(RTCRtpReceiver.getCapabilities('video')?.codecs || []),
		...(RTCRtpReceiver.getCapabilities('audio')?.codecs || []),
	];
	if (!mediaSupport.length) return [];

	const groupedMediaSupport: {
		[key: string]: { title: string; values: string[] };
	} = {};

	// Group the codecs by codec name
	mediaSupport.forEach((codec) => {
		const mimeType = codec.mimeType || 'unknown';
		if (!groupedMediaSupport[mimeType]) {
			groupedMediaSupport[mimeType] = {
				title: mimeType,
				values: [],
			};
		}
		if (codec.sdpFmtpLine) {
			groupedMediaSupport[mimeType].values.push(codec.sdpFmtpLine);
		}
	});

	return Object.entries(groupedMediaSupport || {}).map(([key, value]) => ({
		title: value.title,
		values: value.values,
	}));
};

/**
 * Get settings for the media stream - video track
 */
export const getMediaSettings = (
	mediaStream: MediaStream,
): {
	video: {
		width: number;
		height: number;
		frameRate: number;
		aspectRatio: number;
	};
	audio: {
		sampleRate: number;
		channelCount: number;
		echoCancellation: boolean;
		noiseSuppression: boolean;
		autoGainControl: boolean;
	};
} => {
	const videoTrack = mediaStream?.getVideoTracks()?.[0];
	const audioTrack = mediaStream?.getAudioTracks()?.[0];

	if (!videoTrack || !audioTrack) {
		return;
	}

	const videoSettings = videoTrack.getSettings();
	const audioSettings = audioTrack.getSettings();

	return {
		video: {
			width: videoSettings.width || 0,
			height: videoSettings.height || 0,
			frameRate: videoSettings.frameRate || 0,
			aspectRatio: videoSettings.aspectRatio || 0,
		},
		audio: {
			sampleRate: audioSettings.sampleRate || 0,
			channelCount: audioSettings.channelCount || 0,
			echoCancellation: audioSettings.echoCancellation || false,
			noiseSuppression: audioSettings.noiseSuppression || false,
			autoGainControl: audioSettings.autoGainControl || false,
		},
	};
};

export const getStreamStats = async (
	peerConnection: RTCPeerConnection,
): Promise<{
	transport: { title: string; value: string }[];
	stream: { title: string; value: string }[];
}> => {
	const data = await peerConnection.getStats();
	const stream = [];
	const transport = [];

	// Get the ICE connection type
	const iceConnectionType = getICEConnectionType(data);
	transport.push({
		title: 'ICE Connection',
		value: iceConnectionType,
	});

	const statsObj = {};
	data.forEach((report) => {
		statsObj[report.type + ':' + report.id] = report;

		if (report.type === 'codec') {
			// console.warn('Codec report', report);
			stream.push({
				title: 'Codec (' + report.mimeType.split('/')[0] + ')',
				value: report.mimeType.split('/')[1],
			});
		} else if (report.type === 'inbound-rtp') {
			if (report.kind === 'video') {
				stream.push({
					title: 'Resolution',
					value: report.frameWidth ? report.frameWidth + 'x' + report.frameHeight : 'N/A',
				});
				stream.push({
					title: 'FPS',
					value: report.framesPerSecond,
				});
				if (report.framesDecoded) {
					stream.push({
						title: 'Video Latency',
						value: report.framesDecoded
							? Number(report.totalProcessingDelay / report.framesDecoded).toFixed(3) + 's'
							: 'N/A',
					});
				}
				stream.push({
					title: 'Packets Lost',
					value: report.packetsLost,
				});
				stream.push({
					title: 'Jitter',
					value: report.jitter + 's',
				});

				stream.push({
					title: 'Total Freezes',
					value: report.totalFreezesDuration + 's',
				});

				transport.push({
					title: 'Received (video)',
					value: Number(report.bytesReceived / 1024 / 1024).toFixed(2) + 'MB',
				});
			} else if (report.kind === 'audio') {
				transport.push({
					title: 'Received (audio)',
					value: Number(report.bytesReceived / 1024 / 1024).toFixed(2) + 'MB',
				});
			}
		}
	});

	return { transport, stream };
};

const findStatsReport = (report: RTCStatsReport, type: string): any => {
	return Array.from(report.values()).find((r) => r.type === type);
};
const getICEConnectionType = (
	report: RTCStatsReport,
): 'relay' | 'srflx' | 'prflx' | 'local' | string => {
	const transport = findStatsReport(report, 'transport');
	if (!transport) {
		return 'unknown';
	}
	const iceCandidatePair = report.get(transport?.selectedCandidatePairId);

	if (transport) {
		const localCandidate = report.get(iceCandidatePair.localCandidateId);
		const remoteCandidate = report.get(iceCandidatePair.remoteCandidateId);

		if (localCandidate && remoteCandidate) {
			if (localCandidate.candidateType === 'relay') {
				return 'relay';
			} else if (localCandidate.candidateType === 'srflx') {
				return 'srflx';
			} else if (localCandidate.candidateType === 'prflx') {
				return 'prflx';
			} else if (localCandidate.candidateType === 'host') {
				return 'local';
			}
		}
	}

	return 'unknown';
};
